import Axios from "../../../networking/interceptor";
import {
	driver_active_routes,
	driver_drop_routes,
	driver_get_route_list,
	driver_handover_patients,
	driver_handover_requests,
	driver_handover_targets,
	driver_handover_untempered,
	driver_live_location,
	driver_live_location_deny,
	driver_logout_readiness,
	driver_patient_spotted,
	driver_route_interruptions,
	driver_route_interruptions_heartbeat,
	driver_route_options,
	driver_select_routes,
} from "../../../networking/urlEndPoints";

const normalizeRoutePayload = (routes) => {
	if (Array.isArray(routes)) {
		return routes
			.map((route) => {
				if (route && typeof route === "object") {
					return route.route ?? route.value ?? route.label ?? route.name ?? null;
				}

				return route;
			})
			.filter((route) => route !== null && route !== undefined && `${route}`.trim() !== "");
	}

	if (routes && typeof routes === "object") {
		const route = routes.route ?? routes.value ?? routes.label ?? routes.name ?? null;
		return route ? [route] : [];
	}

	return routes !== null && routes !== undefined && `${routes}`.trim() !== "" ? [routes] : [];
};

export const fetchAvailableRoutes = async (pharmacyId) => {
	const response = await Axios.get(driver_get_route_list, {
		params: { pharmacy_id: pharmacyId },
	});
	return response.data?.data || [];
};

export const fetchDriverRouteOptions = async (pharmacyId) => {
	const response = await Axios.get(driver_route_options, {
		params: { pharmacy_id: pharmacyId },
	});
	return Array.isArray(response.data?.data) ? response.data.data : [];
};

export const selectDriverRoutes = async ({
	pharmacyId,
	routes,
	pharmacistId,
	originalDeliveryPersonnelSignature,
}) => {
	const response = await Axios.post(driver_select_routes, {
		pharmacy_id: pharmacyId,
		routes: normalizeRoutePayload(routes),
		pharmacist_id: pharmacistId || null,
		original_delivery_personnel_signature: originalDeliveryPersonnelSignature || undefined,
	});
	return response.data?.data;
};

export const fetchDriverActiveRoutes = async (pharmacyId) => {
	const response = await Axios.get(driver_active_routes, {
		params: { pharmacy_id: pharmacyId },
	});
	return response.data?.data || [];
};

export const fetchDriverLogoutReadiness = async (pharmacyId) => {
	const response = await Axios.get(driver_logout_readiness, {
		params: { pharmacy_id: pharmacyId },
	});
	return response.data?.data;
};

export const dropDriverRoutes = async ({ pharmacyId, routes }) => {
	const response = await Axios.post(driver_drop_routes, {
		pharmacy_id: pharmacyId,
		routes: normalizeRoutePayload(routes),
	});
	return response.data?.data;
};

export const fetchDriverRouteInterruptionStatus = async (pharmacyId) => {
	const response = await Axios.get(driver_route_interruptions, {
		params: { pharmacy_id: pharmacyId },
	});
	return response.data?.data || null;
};

export const reportDriverRouteInterruption = async (pharmacyId) => {
	const response = await Axios.post(
		`${driver_route_interruptions}/report`,
		pharmacyId ? { pharmacy_id: pharmacyId } : {}
	);
	return Array.isArray(response.data?.data) ? response.data.data : [];
};

export const sendDriverActivityHeartbeat = async ({ pharmacyId, route }) => {
	const response = await Axios.post(driver_route_interruptions_heartbeat, {
		pharmacy_id: pharmacyId,
		route: route || undefined,
	});
	return response.data?.data || null;
};

export const recordDriverActivitySignal = ({ pharmacyId, route }) =>
	sendDriverActivityHeartbeat({
		pharmacyId,
		route,
	}).catch(() => null);

export const resumeDriverRouteInterruption = async (interruptionId, explanation) => {
	const response = await Axios.post(`${driver_route_interruptions}/${interruptionId}/resume`, {
		resume_explanation: explanation,
	});
	return response.data?.data || null;
};

export const fetchDriverHandoverTargets = async (pharmacyId) => {
	const response = await Axios.get(driver_handover_targets, {
		params: { pharmacy_id: pharmacyId },
	});
	return response.data?.data || { drivers: [], assistants: [], supervisors: [], pharmacists: [], instore_pharmacist: [] };
};

export const fetchDriverHandoverPatients = async (pharmacyId) => {
	const response = await Axios.get(driver_handover_patients, {
		params: { pharmacy_id: pharmacyId },
	});
	return Array.isArray(response.data?.data) ? response.data.data : [];
};

export const fetchDriverHandoverUntempered = async (pharmacyId) => {
	const response = await Axios.get(driver_handover_untempered, {
		params: { pharmacy_id: pharmacyId },
	});
	return Array.isArray(response.data?.data) ? response.data.data : [];
};

export const fetchDriverHandoverRequests = async (pharmacyId) => {
	const response = await Axios.get(driver_handover_requests, {
		params: { pharmacy_id: pharmacyId },
	});
	return response.data?.data || [];
};

export const createDriverHandoverRequest = async ({
	pharmacyId,
	targetType,
	targetId,
	sendToAllInstore,
	handoverType,
	patientId,
	patientIds,
	routes,
	winrxDataIds,
	deliveryUnconsumedDoseIds,
	latitude,
	longitude,
	message,
	signatureBase64,
}) => {
	const response = await Axios.post(driver_handover_requests, {
		pharmacy_id: pharmacyId,
		target_type: targetType,
		target_id: targetId ?? null,
		send_to_all_instore: Boolean(sendToAllInstore),
		handover_type: handoverType || undefined,
		patient_id: patientId ?? null,
		patient_ids: Array.isArray(patientIds) && patientIds.length > 1 ? patientIds : undefined,
		routes: normalizeRoutePayload(routes),
		winrx_data_ids: winrxDataIds || [],
		delivery_unconsumed_dose_ids: deliveryUnconsumedDoseIds || [],
		latitude,
		longitude,
		message,
		signature_base64: signatureBase64,
	});
	return response.data?.data;
};

export const acceptDriverHandoverRequest = async (requestId, signatureBase64) => {
	const response = await Axios.post(`${driver_handover_requests}/${requestId}/accept`, {
		signature_base64: signatureBase64,
	});
	return response.data?.data;
};

export const rejectDriverHandoverRequest = async (requestId) => {
	const response = await Axios.post(`${driver_handover_requests}/${requestId}/reject`);
	return response.data?.data;
};

export const notifyPatientSpotted = async ({ pharmacyId, patientId, latitude, longitude }) => {
	const response = await Axios.post(driver_patient_spotted, {
		pharmacy_id: pharmacyId,
		patient_id: patientId,
		latitude,
		longitude,
	});
	return response.data?.data;
};

export const updateDriverLiveLocation = async ({
	pharmacyId,
	route,
	latitude,
	longitude,
	accuracyMeters,
	speedMps,
	heading,
	requestContext,
	requestedByPharmacyUserId,
	requestedByName,
	resolvedAddress,
}) => {
	const response = await Axios.post(driver_live_location, {
		pharmacy_id: pharmacyId,
		route,
		latitude,
		longitude,
		accuracy_meters: accuracyMeters ?? null,
		speed_mps: speedMps ?? null,
		heading: heading ?? null,
		request_context: requestContext ?? null,
		requested_by_pharmacy_user_id: requestedByPharmacyUserId ?? null,
		requested_by_name: requestedByName ?? null,
		resolved_address: resolvedAddress ?? null,
	});
	return response.data?.data;
};

export const denyDriverLiveLocationRequest = async ({
	pharmacyId,
	route,
	requestedByPharmacyUserId,
	requestedByName,
}) => {
	const response = await Axios.post(driver_live_location_deny, {
		pharmacy_id: pharmacyId,
		route,
		requested_by_pharmacy_user_id: requestedByPharmacyUserId ?? null,
		requested_by_name: requestedByName ?? null,
	});
	return response.data?.data;
};
